import { MetricDirection, MetricFormat, type MetricDefinition, type ReportView } from "@gen/tracker/v1/reporting_pb";

import { DASH, formatInt, formatMoney, formatPct, formatPp, relativeChange } from "@/lib/format";
import { allColumns, isAvailable, PRESET_SETS, BAND_ORDER, type Band, type ColumnSpec, type Format } from "@/lib/campaigns-table";
import type { ReportingSchema } from "@/lib/services/reporting-schema-service";
import type { TKey } from "@/lib/i18n";
import { columnHint, formulaHint, type PickerEntry } from "@/lib/column-picker";

/**
 * The report's columns (доски ReportColumns, ReportFormula): the campaigns
 * list's column specs — one definition of every number — plus the
 * workspace's own formulas, which the server serves as metrics of category
 * `custom` and computes like any other. Pure.
 */

/** A column of the report. A workspace formula carries its definition: digits, unit, which way is better. */
export interface ReportColumn extends ColumnSpec {
  custom?: MetricDefinition;
}

/** The built-in column sets, in the order the picker lists them. */
export const BUILTIN_SETS = ["money", "funnel", "quality", "compare"] as const;
export type BuiltinSet = (typeof BUILTIN_SETS)[number];

export function isBuiltinSet(v: string): v is BuiltinSet {
  return (BUILTIN_SETS as readonly string[]).includes(v);
}

/** The keys of a built-in set; a saved set's keys are its view's `metric_keys`. */
export function setKeys(id: string, saved: readonly ReportView[]): string[] | undefined {
  if (isBuiltinSet(id)) return PRESET_SETS[id];
  return saved.find((v) => v.id === id)?.definition?.metricKeys;
}

/** The workspace's formulas as columns: after the money, in the schema's order. */
export function customColumns(schema: ReportingSchema | undefined): ReportColumn[] {
  return (schema?.metrics ?? [])
    .filter((m) => m.category === "custom")
    .map((m) => ({ key: m.key, label: m.label, band: "money" as Band, group: "money" as const, format: customFormat(m), custom: m }));
}

function customFormat(m: MetricDefinition): Format {
  switch (m.format) {
    case MetricFormat.PERCENT:
      return "pct1";
    case MetricFormat.CURRENCY_USD:
      return "unit";
    default:
      return "int";
  }
}

/**
 * The lander's own rates (доска ReportLanders: «CR после ленда» = лиды /
 * LP-клики) — registry metrics the campaigns list does not draw.
 */
const LANDER_COLUMNS: ReportColumn[] = [
  { key: "lander_cr", label: "rep.col.landerCr", band: "conversions", group: "conversions", format: "pct1", dim: true },
  { key: "lander_epc", label: "rep.col.landerEpc", band: "money", group: "money", format: "unit", dim: true },
];

/** Every column the picker offers. */
export function reportColumns(schema: ReportingSchema | undefined): ReportColumn[] {
  const own = new Map(LANDER_COLUMNS.filter((c) => schema?.metricsByKey.has(c.key)).map((c) => [c.key, c]));
  const out: ReportColumn[] = [];
  for (const c of allColumns(schema).filter((x) => isAvailable(x, schema))) {
    if (c.key === "approve_rate" && own.has("lander_cr")) out.push(own.get("lander_cr")!);
    out.push(c);
    if (c.key === "epc" && own.has("lander_epc")) out.push(own.get("lander_epc")!);
  }
  return [...out, ...customColumns(schema)];
}

/**
 * The columns on screen, in band order (Трафик · Конверсии · Деньги), each band
 * keeping the reader's order; a formula sits in «Деньги» after the built-ins.
 * Unknown keys are dropped. Pure.
 */
export function pickColumns(keys: readonly string[], schema: ReportingSchema | undefined): ReportColumn[] {
  const all = new Map(reportColumns(schema).map((c) => [c.key, c]));
  const picked = keys.map((k) => all.get(k)).filter((c): c is ReportColumn => !!c);
  return BAND_ORDER.flatMap((band) => picked.filter((c) => c.band === band));
}

/** Which way a number is better: the schema says, for built-ins and formulas alike. */
export function betterOf(c: ReportColumn, schema: ReportingSchema | undefined): MetricDirection {
  const def = c.custom ?? schema?.metricsByKey.get(c.key);
  if (def) return def.better;
  // Columns the client derives (боты %) — fewer is better.
  return c.key === "bots_pct" ? MetricDirection.LOWER : MetricDirection.UNSPECIFIED;
}

const LOCALE = "ru-RU";

/** A formula's value with its own digits and unit; a percent is a ratio. Pure. */
export function formatCustom(def: Pick<MetricDefinition, "format" | "decimals">, v: number | undefined): string {
  if (v === undefined || !Number.isFinite(v)) return DASH;
  const digits = Math.max(0, Math.min(4, def.decimals));
  const n = def.format === MetricFormat.PERCENT ? v * 100 : v;
  const text = new Intl.NumberFormat(LOCALE, { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(n);
  return def.format === MetricFormat.PERCENT ? `${text}%` : text;
}

/** One cell's text, the CSV's and the screen's alike. Pure. */
export function cellText(c: ReportColumn, v: number | undefined): string {
  if (c.custom) return formatCustom(c.custom, v);
  switch (c.format) {
    case "int":
      return formatInt(v);
    case "pct1":
      return formatPct(v);
    case "pct0":
      return formatPct(v, { digits: 0 });
    case "money":
      return formatMoney(v);
    case "unit":
      return formatMoney(v, { unit: true });
    case "profit":
      return formatMoney(v, { signed: true });
    case "roi":
      return formatPct(v, { digits: 0, signed: true });
    case "outcome":
      return DASH;
  }
}

const isRate = (c: ReportColumn) =>
  c.format === "pct0" || c.format === "pct1" || c.format === "roi" || c.custom?.format === MetricFormat.PERCENT;

/**
 * The change against the comparison period: percentage points for a rate,
 * the money itself for profit, a relative change for everything else. Pure.
 */
export function deltaText(c: ReportColumn, cur: number | undefined, prev: number | undefined): string {
  if (cur === undefined || prev === undefined) return DASH;
  if (isRate(c)) return formatPp(cur - prev);
  if (c.format === "profit") return formatMoney(cur - prev, { signed: true });
  const rel = relativeChange(cur, prev);
  return rel === undefined ? DASH : formatPct(rel, { digits: 0, signed: true });
}

/** «pos» when the change is the good way for this metric, by the schema's `better`. Pure. */
export function deltaTone(better: MetricDirection, cur: number | undefined, prev: number | undefined): "pos" | "neg" | undefined {
  if (cur === undefined || prev === undefined || cur === prev || better === MetricDirection.UNSPECIFIED) return undefined;
  return cur > prev === (better === MetricDirection.HIGHER) ? "pos" : "neg";
}

/** The set the current columns came from, or null when they match none. Pure. */
/** «Деньги» at ≤1440 (`moneyAt` in state.ts): without the bots' share, LP CTR and CPA. */
export const REPORT_MONEY_NARROW: readonly string[] = PRESET_SETS.money.filter((k) => k !== "bots_pct" && k !== "lp_ctr" && k !== "cpa");

export function activeSet(keys: readonly string[], saved: readonly ReportView[]): string | null {
  const same = (a: readonly string[]) => a.length === keys.length && a.every((k, i) => k === keys[i]);
  for (const id of BUILTIN_SETS) if (same(PRESET_SETS[id])) return id;
  if (same(REPORT_MONEY_NARROW)) return "money";
  for (const v of saved) if (v.definition && same(v.definition.metricKeys)) return v.id;
  return null;
}

/**
 * The columns as the picker's entries (доска ReportColumns): the short name,
 * the board's note, a formula's own expression; the goals' counts apart from
 * the conversions (lib/column-picker). Pure.
 */
export function reportPickerEntries(columns: readonly ReportColumn[], t: (k: TKey) => string): PickerEntry[] {
  const labelOf = (key: string) => {
    const c = columns.find((x) => x.key === key);
    return c ? columnLabel(t, c) : undefined;
  };
  return columns.map((c) => ({
    key: c.key,
    label: columnLabel(t, c),
    group: c.custom ? "custom" : c.group === "goals" ? "goals" : c.band,
    hint: c.custom ? (c.custom.formula?.expression ? formulaHint(c.custom.formula.expression, labelOf) : t("cols.hint.formula")) : columnHint(t, c.key),
  }));
}

/** A column's name as the picker and the header write it: a key of the dictionary, or a formula's own label. */
export function columnLabel(t: (k: TKey) => string, c: ReportColumn): string {
  if (c.custom) return c.label;
  return c.label.includes(".") && !c.label.includes(" ") ? t(c.label as TKey) : c.label;
}
